import React from 'react';
import {Autocomplete, TextField} from '@mui/material';
import {CheckBox, CheckBoxOutlineBlank} from '@mui/icons-material';
import {PopperProps} from '@mui/material/Popper';
import {Popper} from '@mui/base';
import {SelectDropdownRenderTags} from './SelectDropdownRenderTags';
import {SelectDropdownRenderOptions} from './SelectDropdownRenderOptions';

export const icon = <CheckBoxOutlineBlank fontSize='small' />;
export const checkedIcon = <CheckBox fontSize='small' />;

const ProductsMultipleSelect = ({
  type,
  label,
  data,
  className,
  handleChange,
  selecteValues,
  selectAll = true,
}: {
  type: string;
  label: string;
  data: string[];
  className: string;
  handleChange: (type: string, selectedIds: string[]) => void;
  selecteValues: string[];
  selectAll?: boolean;
}) => {
  const CustomPopper: React.FC<PopperProps> = props => {
    return <Popper {...props} className={`w-300 ${props.className}`} />;
  };

  const SELECT_ALL_ID = 0;
  const [inputValue, setInputValue] = React.useState('');

  const dedupedData = React.useMemo(
    () => [...new Map(data.map(item => [item, item])).values()],
    [data],
  );

  const getFilteredOptions = React.useCallback(() => {
    if (!inputValue.trim()) return dedupedData;
    return dedupedData.filter(option =>
      String(option).toLowerCase().includes(inputValue.toLowerCase()),
    );
  }, [dedupedData, inputValue]);

  const handleSelectAll = (event: React.MouseEvent) => {
    event.stopPropagation();
    const filteredOptions = getFilteredOptions();
    const allFilteredSelected = filteredOptions.every(option =>
      selecteValues.includes(option),
    );

    if (allFilteredSelected) {
      // Deselect all filtered options
      const newValues = selecteValues.filter(
        value => !filteredOptions.includes(value),
      );
      handleChange(type, newValues);
    } else {
      // Select all filtered options
      const newValues = [...new Set([...selecteValues, ...filteredOptions])];
      handleChange(type, newValues);
    }
  };

  // Use shared SelectDropdownRenderOptions
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: string | number,
  ) =>
    SelectDropdownRenderOptions<string | number>({
      selectAll,
      SELECT_ALL_ID,
      getFilteredOptions,
      handleSelectAll,
      selecteValues,
      option,
      props,
      getOptionLabel: (opt: string | number) =>
        opt === SELECT_ALL_ID ? 'Select All' : String(opt),
    });

  return (
    <Autocomplete
      className={className ? className : ''}
      multiple
      limitTags={1}
      options={selectAll ? [SELECT_ALL_ID, ...dedupedData] : [...dedupedData]}
      autoHighlight
      disableCloseOnSelect
      value={selecteValues}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={option => String(option)}
      isOptionEqualToValue={(option, value) => option === value}
      onChange={(
        _: React.SyntheticEvent<Element, Event>,
        value: (string | number)[],
      ) => {
        const filteredValue = value.filter(
          (v): v is string => typeof v === 'string',
        );
        handleChange(type, filteredValue);
      }}
      filterOptions={(options, state) => {
        const filtered = options.filter(option => {
          if (selectAll && option === SELECT_ALL_ID) return true;
          return String(option)
            .toLowerCase()
            .includes(state.inputValue.toLowerCase());
        });
        return filtered;
      }}
      renderOption={renderOption}
      renderTags={(value, getTagProps) => (
        <SelectDropdownRenderTags value={value} getTagProps={getTagProps} />
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          inputProps={{
            ...params.inputProps,
          }}
          sx={{
            // Add spacing above selected tags
            '.MuiAutocomplete-tag': {
              marginTop: 2,
            },
          }}
        />
      )}
      PopperComponent={CustomPopper}
    />
  );
};

export default React.memo(ProductsMultipleSelect);
